import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Routes, Route, useNavigate } from 'react-router-dom';
import PersonaList from './PersonaList';
import PersonaDetail from './PersonaDetail';
import ErrorBoundary from './ErrorBoundary';
import PersonaAvatar from './PersonaAvatar';
import { useLocation } from 'react-router-dom';
import DemoHub from './DemoHub';
import AppHeader from './AppHeader';
import AppFooter from './AppFooter';
import './demo-styles.css';
import modelsData from './data/models.json';

// Import individual demo components
import Translator from './demos/Translator';
import SentimentAnalysis from './demos/SentimentAnalysis';
import TextToSpeech from './demos/TextToSpeech';
import SpeechToText from './demos/SpeechToText';
import WhatsAppSupportAgent from './demos/WhatsAppSupportAgent';
import WhatsAppBankOfAfricaInfo from './demos/WhatsAppBankOfAfricaInfo';
import RingCentralIntegration from './demos/RingCentralIntegration';
import CallbotSupportAgent from './demos/CallbotSupportAgent';
import SwahiliFrenchTranslator from './demos/SwahiliFrenchTranslator';
import SwahiliFrenchConversation from './demos/SwahiliFrenchConversation';

// Temporarily hidden
// import WebSupportAgent from './demos/WebSupportAgent';

// Declare all variables at the top of the file
let selectedPersona = null;

function App() {
  // All hooks must be at the top, before any early returns!
  const PASSWORD = 'khawarizmi';
  const AUTH_KEY = 'demo_auth';
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [personas, setPersonas] = useState([]);
  
  // Safe location access
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract persona from location.state if available (when navigating from PersonaList)
  const personaInState = location?.state?.persona;
  
  // Try to get personaId from the URL
  const match = location?.pathname?.match(/^\/persona\/(.+)$/);
  const personaId = match ? match[1] : null;
  
  // Find persona from list if available - with defensive check
  const persona = personaInState || (personaId && personas?.length > 0 ? personas.find(p => p._id === personaId) : null);
  // Hooks are already declared at the top

  useEffect(() => {
    const item = localStorage.getItem(AUTH_KEY);
    if (item) {
      try {
        const { ts, pw } = JSON.parse(item);
        if (pw === PASSWORD && Date.now() - ts < 24*60*60*1000) {
          setIsAuthenticated(true);
        }
      } catch (e) {}
    }
    setAuthChecked(true);
  }, []);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/personas`)
      .then(res => setPersonas(res.data))
      .catch(() => setPersonas([]));
  }, []);



  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (editingPersonaId) {
      // Update
      await axios.patch(`${process.env.REACT_APP_API_URL}/persona/${editingPersonaId}`, personaForm);
    } else {
      // Create
      await axios.post(`${process.env.REACT_APP_API_URL}/persona`, personaForm);
    }
    setShowForm(false);
    setEditingPersonaId(null);
    setPersonaForm({
      name: '',
      type: 'prepaid',
      email: '',
      phone_number: '',
      balance: 0,
      puk: ''
    });
    fetchPersonas();
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingPersonaId(null);
    setPersonaForm({
      name: '',
      type: 'prepaid',
      email: '',
      phone_number: '',
      balance: 0,
      puk: ''
    });
  };

  const handleCheckBalance = async () => {
    if (!selectedPersona) return;
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/persona/${selectedPersona._id}/balance`);
      setBalance(res.data.balance);
    } catch {
      setBalance('Error');
    }
    setLoading(false);
  };

  // Fetch balance automatically when persona is selected
  useEffect(() => {
    if (selectedPersona) {
      handleCheckBalance();
      setSelectedTab('infos'); // always show Infos tab first
    }
  }, [selectedPersona]);

  const handleActivateBundle = async () => {
    if (!selectedPersona || !bundleName) return;
    setBundleLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/persona/${selectedPersona._id}/bundles`, {
        name: bundleName,
        expiresAt: bundleExpires || null
      });
      setBundleMessage('Bundle activated!');
      setBundleName('');
      setBundleExpires('');
    } catch {
      setBundleMessage('Error activating bundle');
    }
    setBundleLoading(false);
  };

  const handleRecharge = async () => {
    if (!selectedPersona || !rechargeAmount) return;
    setRechargeLoading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/persona/${selectedPersona._id}/recharge`, {
        amount: rechargeAmount
      });
      setBalance(res.data.balance);
      alert('Recharge successful!');
      setRechargeAmount('');
    } catch {
      alert('Error processing recharge');
    }
    setRechargeLoading(false);
  };

  const handleGetPuk = async () => {
    if (!selectedPersona) return;
    setPukLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/persona/${selectedPersona._id}/puk`);
      setPuk(res.data.puk);
    } catch {
      setPuk('Error');
    }
    setPukLoading(false);
  };

  const handleFetchServices = async () => {
    if (!selectedPersona) return;
    setServiceLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/persona/${selectedPersona._id}/services`);
      setServices(res.data);
    } catch {
      setServices([]);
    }
    setServiceLoading(false);
  };

  const handleToggleService = async (idx) => {
    if (!selectedPersona) return;
    setServiceLoading(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/persona/${selectedPersona._id}/services/${idx}/toggle`);
      await handleFetchServices();
    } catch {
      alert('Error toggling service');
    }
    setServiceLoading(false);
  };

  const getDemoInfo = () => {
    const currentRoute = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const language = searchParams.get('language') || 'tn';
    
    let demoTitle = null;
    let apiDocUrl = null;
    
    switch (currentRoute) {
      case '/demo/translator':
        const translatorModel = modelsData.models.find(model => 
          model.tags.includes('translation') && model.language === language
        );
        demoTitle = translatorModel?.title || 'Translator';
        apiDocUrl = translatorModel?.apiDocUrl;
        break;
        
      case '/demo/sentiment-analysis':
        const sentimentModel = modelsData.models.find(model => 
          model.tags.includes('sentiment') && model.language === language
        );
        demoTitle = sentimentModel?.title || 'Sentiment Analysis';
        apiDocUrl = sentimentModel?.apiDocUrl;
        break;
        
      case '/demo/text-to-speech':
        const ttsModel = modelsData.models.find(model => 
          model.tags.includes('tts') && model.language === language
        );
        demoTitle = ttsModel?.title || 'Text-to-Speech';
        apiDocUrl = ttsModel?.apiDocUrl;
        break;
        
      case '/demo/speech-to-text':
        const sttModel = modelsData.models.find(model => 
          model.tags.includes('stt') && model.language === language
        );
        demoTitle = sttModel?.title || 'Speech-to-Text';
        apiDocUrl = sttModel?.apiDocUrl;
        break;
        
      default:
        break;
    }
    
    return { demoTitle, apiDocUrl };
  };

  // La logique de navigation a été déplacée dans le composant AppHeader
  
  const { demoTitle, apiDocUrl } = getDemoInfo();

  return (
    <ErrorBoundary>
      <div className="app-container" style={{
        minHeight: '100vh',
        height: '100vh',
        background: '#f5f6fa',
        fontFamily: 'Inter, Arial, sans-serif',
        transition: 'background 0.3s',
        maxWidth: '100vw',
        width: '100vw',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <AppHeader demoTitle={demoTitle} apiDocUrl={apiDocUrl} />
        <div className="App" style={{ 
          flex: 1, 
          overflow: 'auto', 
          padding: '1rem',
          minHeight: 0
        }}>
          {/* Routes for the application */}
          <Routes>
            <Route path="/" element={<DemoHub />} />
            <Route path="/demo/translator" element={<Translator />} />
            <Route path="/demo/swahili-french-translator" element={<SwahiliFrenchTranslator />} />
            <Route path="/demo/swahili-french-conversation" element={<SwahiliFrenchConversation />} />
            <Route path="/demo/sentiment-analysis" element={<SentimentAnalysis />} />
            <Route path="/demo/text-to-speech" element={<TextToSpeech />} />
            <Route path="/demo/speech-to-text" element={<SpeechToText />} />
            {/* Temporarily hidden */}
            {/* <Route path="/demo/web-support" element={<WebSupportAgent />} />
            <Route path="/demo/whatsapp-support" element={<WhatsAppSupportAgent />} /> */}
            <Route path="/demo/callbot-support" element={<CallbotSupportAgent />} />
            <Route path="/whatsapp-bankofafrica-info" element={<WhatsAppBankOfAfricaInfo />} />
            <Route path="/ringcentral-integration" element={<RingCentralIntegration />} />
            <Route path="/personas" element={<PersonaList personas={personas} />} />
            <Route path="/persona/:id" element={<PersonaDetail />} />
          </Routes>
        </div>
        <AppFooter apiDocUrl={apiDocUrl} />
      </div>
    </ErrorBoundary>
  );
}
export default App;
